// src/services/partnerAdminService.js
//
// All requests go through the shared `api` axios instance from apiService.js.
// That instance:
//   - Has baseURL = https://staging-api.zelospace.com (or prod equivalent)
//   - Handles JWT auth headers automatically
//   - Handles silent token refresh on 401
//   - Handles FormData (multipart) correctly
//
// There is no need for a separate fetch-based request function or a duplicate
// token refresh queue here. Any previous duplication is removed.

import { api } from "@/services/apiService";

const BASE = "/api/partner-admin";

// ─── Me ───────────────────────────────────────────────────────────────────────

export const getPartnerMe = () =>
  api.get(`${BASE}/me/`).then(r => r.data);

// ─── Dashboard ────────────────────────────────────────────────────────────────

export const getPartnerDashboard = () =>
  api.get(`${BASE}/dashboard/`).then(r => r.data);

// ─── Competitions ─────────────────────────────────────────────────────────────

export const getPartnerCompetitions = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api.get(`${BASE}/competitions/${qs ? "?" + qs : ""}`).then(r => r.data);
};

export const getPartnerCompetition = (id) =>
  api.get(`${BASE}/competitions/${id}/`).then(r => r.data);

export const createPartnerCompetition = (data) =>
  api.post(`${BASE}/competitions/`, data).then(r => r.data);

export const updatePartnerCompetition = (id, data) =>
  api.put(`${BASE}/competitions/${id}/`, data).then(r => r.data);

export const patchPartnerCompetition = (id, data) =>
  api.patch(`${BASE}/competitions/${id}/`, data).then(r => r.data);

export const deletePartnerCompetition = (id) =>
  api.delete(`${BASE}/competitions/${id}/`).then(r => r.data);

// ─── Submissions ──────────────────────────────────────────────────────────────

export const getPartnerSubmissions = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api.get(`${BASE}/submissions/${qs ? "?" + qs : ""}`).then(r => r.data);
};

export const approveSubmission = (id) =>
  api.patch(`${BASE}/submissions/${id}/approve/`).then(r => r.data);

export const rejectSubmission = (id) =>
  api.patch(`${BASE}/submissions/${id}/reject/`).then(r => r.data);

// ─── Users ────────────────────────────────────────────────────────────────────

export const getPartnerUsers = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return api.get(`${BASE}/users/${qs ? "?" + qs : ""}`).then(r => r.data);
};

export const getPartnerUser = (id) =>
  api.get(`${BASE}/users/${id}/`).then(r => r.data);

export const banPartnerUser = (id) =>
  api.post(`${BASE}/users/${id}/ban/`).then(r => r.data);

// Returns a URL string for direct download — kept as-is since it's not a fetch call
export const exportPartnerUsersUrl = () =>
  `${api.defaults.baseURL || ""}${BASE}/users/export/`;

// ─── Team ─────────────────────────────────────────────────────────────────────

export const getPartnerTeam = () =>
  api.get(`${BASE}/team/`).then(r => r.data);

export const updateTeamMemberRole = (id, role) =>
  api.patch(`${BASE}/team/${id}/`, { role }).then(r => r.data);

export const removeTeamMember = (id) =>
  api.delete(`${BASE}/team/${id}/`).then(r => r.data);

export const searchTeamUsers = (q) =>
  api.get(`${BASE}/team/search/?q=${encodeURIComponent(q)}`).then(r => r.data);

export const addTeamMember = (user_id, role) =>
  api.post(`${BASE}/team/`, { user_id, role }).then(r => r.data);

// ─── Settings ─────────────────────────────────────────────────────────────────

export const getPartnerSettings = () =>
  api.get(`${BASE}/settings/`).then(r => r.data);

export const updatePartnerSettings = (data) =>
  api.patch(`${BASE}/settings/`, data).then(r => r.data);